import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, Phone, Mail, MapPin, IdCard, Bluetooth, BellRing, Calendar,
  Briefcase, ShieldCheck, HeartPulse, KeyRound, Check,
} from 'lucide-react';
import {
  isPushSupported, getPermissionState, getExistingSubscription,
  subscribeToPush, registerServiceWorker,
} from '../utils/pushNotifications';

const PERSON_TYPE_LABELS = { driver: 'Driver', staff: 'Staff', passenger: 'Passenger' };

const InfoRow = ({ icon: Icon, label, value, testid }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-words">{value}</p>
      </div>
    </div>
  );
};

// One-tap browser push enablement so drivers/staff can receive confirmations
// (e.g. "Are you driving …?") without hunting through Settings.
const PushEnableBanner = ({ api }) => {
  const [state, setState] = useState('checking'); // checking|enable|subscribed|denied|unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) { setState('unsupported'); return; }
      const perm = getPermissionState();
      if (perm === 'denied') { setState('denied'); return; }
      const sub = await getExistingSubscription();
      setState(sub ? 'subscribed' : 'enable');
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await registerServiceWorker();
      await subscribeToPush(api);
      setState('subscribed');
      toast.success('Notifications enabled on this device');
    } catch (e) {
      const perm = getPermissionState();
      if (perm === 'denied') setState('denied');
      toast.error('Could not enable notifications');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'checking' || state === 'unsupported') return null;
  if (state === 'subscribed') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-900 px-3 py-2 text-sm text-green-700 dark:text-green-400" data-testid="push-enabled-banner">
        <Check className="w-4 h-4 shrink-0" /> Notifications are enabled on this device.
      </div>
    );
  }
  if (state === 'denied') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 px-3 py-2 text-sm text-amber-700 dark:text-amber-400" data-testid="push-denied-banner">
        Notifications are blocked for this site. Allow notifications in your browser settings to receive alerts.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-900 px-3 py-2.5" data-testid="push-enable-banner">
      <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
        <BellRing className="w-4 h-4 shrink-0" />
        Turn on notifications so you never miss an alert on this device.
      </div>
      <Button size="sm" className="h-8 bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={enable} disabled={busy} data-testid="push-enable-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Enable notifications'}
      </Button>
    </div>
  );
};

export default function MyContact() {
  const { api, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/me/person-profile');
      setProfile(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load your contact details');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  useEffect(() => {
    if (!profile?.photo) { setPhotoUrl(null); return undefined; }
    let cancelled = false; let obj = null;
    api.get('/me/person-photo', { responseType: 'blob' })
      .then((r) => { if (!cancelled) { obj = URL.createObjectURL(r.data); setPhotoUrl(obj); } })
      .catch(() => { if (!cancelled) setPhotoUrl(null); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.photo?.id]);

  const initials = (profile?.full_name || user?.full_name || 'U')
    .split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  const typeLabel = PERSON_TYPE_LABELS[profile?.person_type] || 'Person';
  const beacon = profile?.personal_beacon;
  const rfid = profile?.rfid_ibutton_card;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4" data-testid="my-contact-page">
      <div>
        <h1 className="text-2xl font-bold">My Contact</h1>
        <p className="text-sm text-muted-foreground">Your contact details as recorded by your fleet operator.</p>
      </div>

      <PushEnableBanner api={api} />

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" /></div>
      ) : error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="my-contact-error">{error}</div>
      ) : profile ? (
        <>
          <Card data-testid="my-contact-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                {photoUrl ? (
                  <img src={photoUrl} alt={profile.full_name} className="w-12 h-12 rounded-full object-cover border shrink-0" data-testid="my-contact-photo" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#2563eb] text-white flex items-center justify-center font-bold text-lg shrink-0">{initials}</div>
                )}
                <div className="min-w-0">
                  <CardTitle className="text-lg truncate" data-testid="my-contact-name">{profile.full_name}</CardTitle>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <Badge variant="outline" className="capitalize" data-testid="my-contact-type">{typeLabel}</Badge>
                    {profile.driver_eligible && (
                      <Badge variant="outline" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" data-testid="my-contact-driver-eligible">Eligible to drive</Badge>
                    )}
                    {profile.status && <Badge variant="outline" className="capitalize">{profile.status}</Badge>}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <InfoRow icon={Mail} label="Email" value={profile.email} testid="my-contact-email" />
              <InfoRow icon={Phone} label="Phone" value={profile.phone} testid="my-contact-phone" />
              <InfoRow icon={MapPin} label="Address" value={profile.address} testid="my-contact-address" />
              <InfoRow icon={Calendar} label="Date of birth" value={profile.date_of_birth} testid="my-contact-dob" />
              <InfoRow icon={Calendar} label="Date started" value={profile.date_started} testid="my-contact-date-started" />
              <InfoRow icon={IdCard} label={profile.person_type === 'passenger' ? 'Passenger ID' : 'Employee ID'} value={profile.employee_id || profile.passenger_id} testid="my-contact-employee-id" />
              {profile.person_type === 'staff' && (
                <InfoRow icon={Briefcase} label="Department / Role" value={[profile.department, profile.role].filter(Boolean).join(' — ')} testid="my-contact-department" />
              )}
              {profile.license_number && (
                <InfoRow icon={ShieldCheck} label={`License${profile.license_expiry ? ` (expires ${profile.license_expiry})` : ''}`} value={profile.license_number} testid="my-contact-license" />
              )}
              {profile.special_requirements && (
                <InfoRow icon={HeartPulse} label="Special requirements" value={profile.special_requirements} testid="my-contact-special-requirements" />
              )}
            </CardContent>
          </Card>

          {(profile.emergency_contact_name || profile.emergency_contact_phone) && (
            <Card data-testid="my-contact-emergency-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Emergency Contact</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <InfoRow icon={HeartPulse} label="Name" value={profile.emergency_contact_name} testid="my-contact-emergency-name" />
                <InfoRow icon={Phone} label="Phone" value={profile.emergency_contact_phone} testid="my-contact-emergency-phone" />
              </CardContent>
            </Card>
          )}

          {(beacon || rfid) && (
            <Card data-testid="my-contact-credentials-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Assigned Credentials</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                {beacon && (
                  <InfoRow icon={Bluetooth} label="Personal ID Beacon" value={beacon.asset_name || beacon.name || beacon.imei} testid="my-contact-beacon" />
                )}
                {rfid && (
                  <InfoRow icon={KeyRound} label="RFID / iButton Card" value={rfid.label || rfid.card_value} testid="my-contact-rfid" />
                )}
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-muted-foreground" data-testid="my-contact-login-note">
            Signed in as {profile.login_email}. If any details are wrong, contact your fleet operator.
          </p>
        </>
      ) : null}
    </div>
  );
}
